import {
    AfterViewInit,
    Component,
    OnDestroy,
    OnInit,
    ViewChild,
} from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { environment } from 'src/environments/environment';
import { HttpClient } from '@angular/common/http';
import { Subject } from 'rxjs';
import { DataTableDirective } from 'angular-datatables';
import { helper } from 'src/environments/helper';
import { AuditLogsService } from './audit-logs.service';
import { AuditLogItem } from './audit-logs.interface';
import Swal from 'sweetalert2';

@Component({
    selector: 'app-audit-logs',
    templateUrl: './audit-logs.component.html',
    styleUrls: ['./audit-logs.component.scss'],
})
export class AuditLogsComponent implements OnInit, OnDestroy, AfterViewInit {
    @ViewChild(DataTableDirective, { static: false })
    dtElement!: DataTableDirective;
    helper = helper;

    isLoading = false;
    showCard = false;
    isExporting = false;

    // Filters
    dateFrom: string = '';
    dateTo: string = '';
    actionFilter: string = '';
    certificateFilter: string = '';
    searchFilter: string = '';

    // Dropdown options
    actions: {value: string, label: string}[] = [];

    dtOptions: any = {};
    dtTrigger: Subject<any> = new Subject<any>();

    constructor(
        private http: HttpClient,
        private dialog: MatDialog,
        private auditLogsService: AuditLogsService
    ) {}

    ngOnInit(): void {
        this.loadActions();
        this.renderDT();
    }

    ngOnDestroy(): void {
        this.dtTrigger.unsubscribe();
    }

    ngAfterViewInit(): void {
        this.dtTrigger.next('');
    }

    loadActions(): void {
        this.auditLogsService.getActions().subscribe({
            next: (response) => {
                if (response.actions) {
                    this.actions = response.actions;
                }
            },
            error: (err) => {
                console.error('Error loading actions:', err);
            }
        });
    }

    renderDT(): void {
        const self = this;
        this.dtOptions = {
            serverSide: true,
            searching: false,
            responsive: true,
            pageLength: 25,
            order: [[0, 'desc']],

            ajax: (dataTablesParameters: any, callback: any) => {
                self.isLoading = true;

                const params: any = {
                    draw: dataTablesParameters.draw,
                    start: dataTablesParameters.start,
                    length: dataTablesParameters.length,
                };

                // Sorting
                if (dataTablesParameters.order && dataTablesParameters.order.length > 0) {
                    const orderCol = dataTablesParameters.order[0].column;
                    const orderDir = dataTablesParameters.order[0].dir;
                    const columns = ['id', 'action', 'request_id', 'certificate_slug', 'user_name', 'client_ip', 'created_at'];
                    params.sort_by = columns[orderCol] || 'id';
                    params.sort_dir = orderDir;
                }

                // Filters
                if (self.dateFrom) params.date_from = self.dateFrom;
                if (self.dateTo) params.date_to = self.dateTo;
                if (self.actionFilter) params.action = self.actionFilter;
                if (self.certificateFilter) params.certificate_slug = self.certificateFilter;
                if (self.searchFilter) params.search = self.searchFilter;

                self.auditLogsService.getAuditLogs(params).subscribe({
                    next: (resp: any) => {
                        self.isLoading = false;
                        callback({
                            recordsTotal: resp.recordsTotal || 0,
                            recordsFiltered: resp.recordsFiltered || 0,
                            data: resp.data || [],
                        });
                    },
                    error: (err) => {
                        self.isLoading = false;
                        console.error('Error fetching audit logs:', err);
                        callback({
                            recordsTotal: 0,
                            recordsFiltered: 0,
                            data: [],
                        });
                    }
                });
            },
            language: environment.config.romanianLanguageDataTable,
            columns: [
                {
                    title: 'ID',
                    data: 'id',
                    width: '60px',
                },
                {
                    title: 'Actiune',
                    data: 'action',
                    render: (data: string) => {
                        const actionColors: Record<string, string> = {
                            'sign': 'primary',
                            'sign_request': 'primary',
                            'certificate_access': 'info',
                            'certificate_check': 'info',
                            'health_check': 'secondary',
                            'error': 'danger',
                            'auth_failed': 'danger',
                        };
                        const color = actionColors[data?.toLowerCase()] || 'secondary';
                        return `<span class="badge bg-${color}">${data || '-'}</span>`;
                    },
                },
                {
                    title: 'Request ID',
                    data: 'request_id',
                    render: (data: string) => {
                        if (!data) return '-';
                        return `<span title="${data}">${data.substring(0, 8)}...</span>`;
                    },
                },
                {
                    title: 'Certificat',
                    data: 'certificate_slug',
                    render: (data: string) => data || '-',
                },
                {
                    title: 'User',
                    data: 'user_name',
                    render: (data: string) => data || '-',
                },
                {
                    title: 'IP',
                    data: 'client_ip',
                    orderable: false,
                    render: (data: string) => data || '-',
                },
                {
                    title: 'Data',
                    data: 'created_at',
                    render: (data: string) => {
                        if (!data) return '-';
                        try {
                            const date = new Date(data);
                            return date.toLocaleString('ro-RO', {
                                day: '2-digit',
                                month: '2-digit',
                                year: 'numeric',
                                hour: '2-digit',
                                minute: '2-digit'
                            });
                        } catch {
                            return data;
                        }
                    },
                },
                {
                    title: 'Actiuni',
                    data: null,
                    orderable: false,
                    className: 'all',
                    render: (row: any) => {
                        return `
                        <div class="d-flex align-center justify-content-left">
                            <button class="action-btn view ms-1" title="Vizualizeaza detalii" data-id="${row.id}">
                                <i class="fa-solid fa-eye"></i>
                            </button>
                        </div>
                        `;
                    },
                },
            ],
            rowCallback: (row: Node, data: AuditLogItem) => {
                const lastTd = (row as HTMLElement).querySelector('td:last-child');
                const viewBtn = lastTd?.querySelector('.view') as HTMLElement;

                if (viewBtn) {
                    viewBtn.addEventListener('click', () => {
                        this.viewDetails(data);
                    });
                }

                return row;
            },
        };
    }

    rerenderDT(paginate: boolean = false): void {
        if (this.dtElement && this.dtElement.dtInstance) {
            this.dtElement.dtInstance.then((dtInstance: DataTables.Api) => {
                dtInstance.draw(paginate);
            });
        }
        this.showCard = false;
    }

    toggleCard(): void {
        this.showCard = !this.showCard;
    }

    resetForm(): void {
        this.dateFrom = '';
        this.dateTo = '';
        this.actionFilter = '';
        this.certificateFilter = '';
        this.searchFilter = '';
        this.rerenderDT();
        this.showCard = true;
    }

    applyFilters(): void {
        this.rerenderDT(true);
    }

    viewDetails(data: AuditLogItem): void {
        this.auditLogsService.getAuditLogDetail(data.id).subscribe({
            next: (response: any) => {
                const detail = response.data || response;
                const actionColors: Record<string, string> = {
                    'sign_start': '#17a2b8',
                    'sign_success': '#28a745',
                    'sign_failed': '#dc3545',
                    'sts_auth_success': '#28a745',
                    'sts_auth_failed': '#dc3545',
                    'sts_sign_success': '#28a745',
                    'sts_sign_failed': '#dc3545',
                    'file_stored': '#6c757d',
                    'file_retrieved': '#6c757d',
                    'file_deleted': '#ffc107',
                    'rate_limit_exceeded': '#dc3545',
                    'rate_limit_check': '#17a2b8',
                    'cleanup_run': '#6c757d',
                    'backup_run': '#6c757d',
                    'api_auth_success': '#28a745',
                    'api_auth_failed': '#dc3545',
                };
                const actionColor = actionColors[detail.action?.toLowerCase()] || '#6c757d';

                const formatDate = (dateStr: string) => {
                    if (!dateStr) return '-';
                    try {
                        return new Date(dateStr).toLocaleString('ro-RO', {
                            day: '2-digit', month: '2-digit', year: 'numeric',
                            hour: '2-digit', minute: '2-digit', second: '2-digit'
                        });
                    } catch { return dateStr; }
                };

                const formatDetails = (details: any) => {
                    if (!details) return '-';
                    try {
                        const formatted = JSON.stringify(details, null, 2);
                        return `<pre style="background: #f8f9fa; padding: 10px; border-radius: 4px; font-size: 11px; max-height: 200px; overflow: auto; text-align: left; margin: 0;">${formatted}</pre>`;
                    } catch {
                        return String(details);
                    }
                };

                const htmlContent = `
                    <div style="text-align: left; font-size: 14px;">
                        <table style="width: 100%; border-collapse: collapse;">
                            <tr>
                                <td style="padding: 8px; border-bottom: 1px solid #eee; font-weight: 600; width: 140px;">ID:</td>
                                <td style="padding: 8px; border-bottom: 1px solid #eee;">${detail.id || '-'}</td>
                            </tr>
                            <tr>
                                <td style="padding: 8px; border-bottom: 1px solid #eee; font-weight: 600;">Actiune:</td>
                                <td style="padding: 8px; border-bottom: 1px solid #eee;">
                                    <span style="background: ${actionColor}; color: white; padding: 3px 10px; border-radius: 4px; font-size: 12px;">
                                        ${detail.action || '-'}
                                    </span>
                                </td>
                            </tr>
                            <tr>
                                <td style="padding: 8px; border-bottom: 1px solid #eee; font-weight: 600;">Request ID:</td>
                                <td style="padding: 8px; border-bottom: 1px solid #eee; font-family: monospace; font-size: 12px;">${detail.request_id || '-'}</td>
                            </tr>
                            <tr>
                                <td style="padding: 8px; border-bottom: 1px solid #eee; font-weight: 600;">Certificat:</td>
                                <td style="padding: 8px; border-bottom: 1px solid #eee;">${detail.certificate_slug || '-'}</td>
                            </tr>
                            <tr>
                                <td style="padding: 8px; border-bottom: 1px solid #eee; font-weight: 600;">Utilizator:</td>
                                <td style="padding: 8px; border-bottom: 1px solid #eee;">${detail.user_name || '-'} ${detail.user_id ? '(ID: ' + detail.user_id + ')' : ''}</td>
                            </tr>
                            <tr>
                                <td style="padding: 8px; border-bottom: 1px solid #eee; font-weight: 600;">IP Client:</td>
                                <td style="padding: 8px; border-bottom: 1px solid #eee;">${detail.client_ip || '-'}</td>
                            </tr>
                            <tr>
                                <td style="padding: 8px; border-bottom: 1px solid #eee; font-weight: 600;">User Agent:</td>
                                <td style="padding: 8px; border-bottom: 1px solid #eee; font-size: 11px; word-break: break-all;">${detail.user_agent || '-'}</td>
                            </tr>
                            <tr>
                                <td style="padding: 8px; border-bottom: 1px solid #eee; font-weight: 600;">Data:</td>
                                <td style="padding: 8px; border-bottom: 1px solid #eee;">${formatDate(detail.created_at)}</td>
                            </tr>
                            ${detail.document_id ? `
                            <tr>
                                <td style="padding: 8px; border-bottom: 1px solid #eee; font-weight: 600;">Document ID:</td>
                                <td style="padding: 8px; border-bottom: 1px solid #eee;">${detail.document_id}</td>
                            </tr>
                            ` : ''}
                            ${detail.document_registratura_id ? `
                            <tr>
                                <td style="padding: 8px; border-bottom: 1px solid #eee; font-weight: 600;">Registratura ID:</td>
                                <td style="padding: 8px; border-bottom: 1px solid #eee;">${detail.document_registratura_id}</td>
                            </tr>
                            ` : ''}
                            ${detail.details ? `
                            <tr>
                                <td style="padding: 8px; border-bottom: 1px solid #eee; font-weight: 600; vertical-align: top;">Detalii:</td>
                                <td style="padding: 8px; border-bottom: 1px solid #eee;">${formatDetails(detail.details)}</td>
                            </tr>
                            ` : ''}
                        </table>
                    </div>
                `;

                Swal.fire({
                    title: '<i class="fa-solid fa-clipboard-list"></i> Detalii Audit Log',
                    html: htmlContent,
                    width: 650,
                    confirmButtonText: 'Inchide',
                    confirmButtonColor: '#3085d6',
                });
            },
            error: (err: any) => {
                console.error('Error fetching audit log detail:', err);
                Swal.fire({
                    title: 'Eroare',
                    text: 'Nu s-au putut incarca detaliile.',
                    icon: 'error',
                    confirmButtonText: 'Inchide',
                });
            }
        });
    }

    exportCsv(): void {
        this.isExporting = true;
        const params: any = {};
        if (this.dateFrom) params.date_from = this.dateFrom;
        if (this.dateTo) params.date_to = this.dateTo;
        if (this.actionFilter) params.action = this.actionFilter;
        if (this.certificateFilter) params.certificate_slug = this.certificateFilter;
        if (this.searchFilter) params.search = this.searchFilter;

        this.auditLogsService.exportCsv(params).subscribe({
            next: (blob) => {
                this.isExporting = false;
                const url = window.URL.createObjectURL(blob);
                const a = document.createElement('a');
                a.href = url;
                a.download = `audit-logs-${new Date().toISOString().split('T')[0]}.csv`;
                document.body.appendChild(a);
                a.click();
                document.body.removeChild(a);
                window.URL.revokeObjectURL(url);
            },
            error: (err) => {
                this.isExporting = false;
                console.error('Error exporting CSV:', err);
            }
        });
    }
}
